// Article page helpers.
// <s-article-toc data-content="#article-body">: builds an "On this page" list from the body's h2/h3,
//   gives each heading an id, and marks the section currently being read with aria-current.
//   Hides its container (data-toc-wrapper ancestor) when the article has fewer than two headings.
// <s-reading-progress data-content="#article-body">: fills its [data-bar] child as the body is read.

const slugify = (text) =>
  text
    .toLowerCase()
    .trim()
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'section';

class ArticleToc extends HTMLElement {
  connectedCallback() {
    const body = document.querySelector(this.dataset.content);
    const list = this.querySelector('[data-toc-list]');
    const wrapper = this.closest('[data-toc-wrapper]') || this;
    if (!body || !list) return;

    // Content pasted from web pages or AI chat tools carries wrapper <div>s and foreign classes/styles.
    // Strip them so every post renders with the same article layout.
    const rich = body.querySelector('.richtext') || body;
    for (const el of rich.querySelectorAll('[class], [style], [data-testid], [id]')) {
      el.removeAttribute('class');
      el.removeAttribute('style');
      el.removeAttribute('data-testid');
      el.removeAttribute('id');
    }
    for (const div of [...rich.querySelectorAll('div')].reverse()) {
      const hasBlockChild = [...div.children].some((c) => /^(DIV|P|UL|OL|H[1-6]|TABLE|BLOCKQUOTE|FIGURE|IMG)$/.test(c.tagName));
      if (hasBlockChild) {
        div.replaceWith(...div.childNodes);
      } else {
        const p = document.createElement('p');
        p.append(...div.childNodes);
        div.replaceWith(p);
      }
    }

    // Posts written with bold paragraphs as subheadings (<p><strong>Title</strong></p>) instead of real
    // headings: promote those paragraphs to h2 so they get heading styling and a TOC entry.
    if (body.querySelectorAll('h2, h3').length < 2) {
      for (const p of body.querySelectorAll('p')) {
        const text = p.textContent.trim();
        const only = p.children.length === 1 ? p.children[0] : null;
        if (!text || text.length > 120 || !only || !/^(STRONG|B)$/.test(only.tagName)) continue;
        if (only.textContent.trim() !== text) continue;
        const h2 = document.createElement('h2');
        h2.innerHTML = only.innerHTML;
        p.replaceWith(h2);
      }
    }

    const headings = [...body.querySelectorAll('h2, h3')].filter((h) => h.textContent.trim());
    if (headings.length < 2) {
      wrapper.hidden = true;
      // Let the article grid collapse to a single centered column when there is no TOC.
      if (wrapper.parentElement) wrapper.parentElement.dataset.noToc = '';
      return;
    }

    const used = new Set();
    const links = new Map();
    for (const heading of headings) {
      if (!heading.id) {
        let id = slugify(heading.textContent);
        let n = 2;
        while (used.has(id) || document.getElementById(id)) id = `${slugify(heading.textContent)}-${n++}`;
        heading.id = id;
      }
      used.add(heading.id);
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent.trim();
      link.className = this.dataset.linkClass || '';
      if (heading.tagName === 'H3') item.className = this.dataset.subClass || '';
      item.append(link);
      list.append(item);
      links.set(heading, link);
    }

    const setActive = (heading) => {
      for (const [h, link] of links) {
        if (h === heading) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      }
    };
    setActive(headings[0]);

    // The active section is the last heading that has scrolled past the reading line under the sticky header.
    let frame = 0;
    this._onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        let current = headings[0];
        for (const h of headings) {
          if (h.getBoundingClientRect().top - 140 <= 0) current = h;
        }
        setActive(current);
      });
    };
    window.addEventListener('scroll', this._onScroll, { passive: true });
  }

  disconnectedCallback() {
    if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
  }
}

class ReadingProgress extends HTMLElement {
  #frame = 0;

  connectedCallback() {
    this.body = document.querySelector(this.dataset.content);
    this.bar = this.querySelector('[data-bar]');
    if (!this.body || !this.bar) return;
    this._update = () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => {
        const rect = this.body.getBoundingClientRect();
        const total = rect.height - window.innerHeight * 0.6;
        const read = Math.min(Math.max(-rect.top + window.innerHeight * 0.2, 0), Math.max(total, 1));
        const ratio = total > 0 ? read / total : 1;
        this.bar.style.transform = `scaleX(${Math.min(Math.max(ratio, 0), 1)})`;
      });
    };
    this._update();
    window.addEventListener('scroll', this._update, { passive: true });
    window.addEventListener('resize', this._update, { passive: true });
  }

  disconnectedCallback() {
    if (!this._update) return;
    window.removeEventListener('scroll', this._update);
    window.removeEventListener('resize', this._update);
  }
}

if (!customElements.get('s-article-toc')) customElements.define('s-article-toc', ArticleToc);
if (!customElements.get('s-reading-progress')) customElements.define('s-reading-progress', ReadingProgress);

// Phones (mobile pass, merchant 2026-11-01): the comments fold starts closed unless the visitor came for the comments
// (a #comments / #comment link, ?comment_posted=1, or the form returned an error). ≥768px it always stays open.
{
  const fold = document.querySelector('#comments > details.fz-afold--comments');
  if (fold && matchMedia('(max-width: 47.99rem)').matches) {
    const wanted = /comment/i.test(location.hash) || /comment_posted=1/.test(location.search) || fold.querySelector('[role="alert"], [aria-invalid="true"]');
    if (!wanted) {
      fold.open = false;
      fold.removeAttribute('data-expanded');
    }
  }
  // ≥768px the head is a plain heading: never fold.
  fold?.querySelector(':scope > summary')?.addEventListener('click', (event) => {
    if (!matchMedia('(max-width: 47.99rem)').matches) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, { capture: true });
}
